import { connect } from "react-redux"

// component functions
import * as OwnFunctions from './ContractAccess.Form.Functions'
import { MessageBarBody, MessageBarTitle, Switch, Text } from "@fluentui/react-components"
import { useEffect, useState } from "react"
import { NewContractAccess } from "../../../../models/Entities/Contracts/NewContractAccess"
import { ContractAccess } from "../../../../models/Entities/Contracts/ContractAccess"
import UserSelectorComponent from "../../../../commons/component/Inputs/UserSelector/UserSelector.Component"
import { User } from "../../../../models/Entities/Authentication/User"
import { GuideDrawer, GuideDrawerSection } from "../../../../theme/components/GuideDrawer"
import { GuideField } from "../../../../theme/components/GuideField"
import { GuideMessageBar } from "../../../../theme/components/GuideMessageBar"

interface FormValidation {
    success: boolean;
    errors: string[];
}

const ContractAccessFormComponent = (props: OwnFunctions.Props) => {

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<FormValidation | undefined>();
    const [userAlreadySelected, setUserAlreadySelected] = useState<boolean>(false);

    // form data
    const [UserId, setUserId] = useState<string | undefined>();
    const [ReadAccess, setReadAccess] = useState<boolean>(false);
    const [WriteAccess, setWriteAccess] = useState<boolean>(false);

    // initialize form from CurrentAccess when editing
    useEffect(() => {
        if (props.CurrentAccess) {
            setUserId(props.CurrentAccess.userID);
            setReadAccess(props.CurrentAccess.readAccess);
            setWriteAccess(props.CurrentAccess.writeAccess);
        } else {
            setUserId(undefined);
            setReadAccess(false);
            setWriteAccess(false);
        }
    }, [props.CurrentAccess]);

    // write access implies read access
    const handleWriteAccessChange = (checked: boolean) => {
        setWriteAccess(checked);
        if (checked) setReadAccess(true);
    };

    const handleReadAccessChange = (checked: boolean) => {
        setReadAccess(checked);
        if (!checked) setWriteAccess(false);
    };

    const onSelectUser = (users: User[]) => {
        if (users.length > 0) {
            const user = users[0];
            const alreadySelected = props.SelectedUsersIds?.find(a => a === user.userId);
            setUserAlreadySelected(!!alreadySelected);
        } else {
            setUserAlreadySelected(false);
        }
        setUserId(users[0]?.userId);
    }

    useEffect(() => {
        if (props.error) {
            setSubmittedForm(false);
        }
    }, [props.error]);

    useEffect(() => {
        if (props.isSavingSuccessfully) {
            ResetForm();
            props.dismissPanel();
        }
    }, [props.isSavingSuccessfully]);

    // form validation
    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;
        let FormValidation: FormValidation = { success: true, errors: [] };

        if (!UserId) {
            FormValidation.errors.push("Debe seleccionar un usuario");
            validationResult = false;
        }

        if (!ReadAccess && !WriteAccess) {
            FormValidation.errors.push("Debe otorgar al menos un permiso (lectura o escritura)");
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    // reset form
    const ResetForm = () => {
        setSubmittedForm(false);
        setUserId(undefined);
        setReadAccess(false);
        setWriteAccess(false);
        setUserAlreadySelected(false);
        setFormValidationStatus(undefined);
    };

    // events
    const HandleCancelForm = () => {
        ResetForm();
        props.dismissPanel();
    };

    const HandleSubmitForm = () => {
        setSubmittedForm(true);

        if (ValidateForm()) {
            if (!props.CurrentAccess) {
                const newAccess: NewContractAccess = {
                    contractID: props.ContractId,
                    userID: UserId || '',
                    readAccess: ReadAccess,
                    writeAccess: WriteAccess
                };
                props.CreateContractAccess(newAccess);
            } else {
                const access: ContractAccess = {
                    id: props.CurrentAccess.id,
                    contractID: props.ContractId,
                    userID: props.CurrentAccess.userID,
                    userName: props.CurrentAccess.userName,
                    readAccess: ReadAccess,
                    writeAccess: WriteAccess
                };
                props.UpdateContractAccess(access);
            }
        } else {
            setSubmittedForm(false);
        }
    };

    const isEditMode = !!props.CurrentAccess;

    return (
        <GuideDrawer
            open={props.isOpen}
            size="small"
            eyebrow={props.Contract?.contractNumber || undefined}
            title={isEditMode ? 'Editar permisos de acceso' : 'Agregar acceso al contrato'}
            onDismiss={HandleCancelForm}
            testId="contract-access-drawer"
            secondaryAction={{ label: 'Cancelar', onClick: HandleCancelForm, disabled: submittedForm }}
            primaryAction={{
                label: isEditMode ? 'Actualizar' : 'Guardar',
                onClick: HandleSubmitForm,
                disabled: submittedForm || (!isEditMode && userAlreadySelected),
                loading: props.isSaving
            }}
        >
            {FormValidationStatus?.success === false && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>Verifique la información suministrada</MessageBarTitle>
                        <ul>
                            {FormValidationStatus.errors.map((error: string, index: number) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {props.failOnSaving === true && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{props.error?.ErrorMessage || 'Ocurrió un error inesperado'}</MessageBarTitle>
                        {props.error?.Errors && props.error.Errors.length > 0 && (
                            <ul>
                                {props.error.Errors.map((error: string, index: number) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        )}
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {!isEditMode && (
                <>
                    <UserSelectorComponent
                        onChangeSelection={onSelectUser}
                        UserSelected={undefined}
                        SelectedUsersIds={[]}
                        fieldName={"Seleccione un usuario"}
                        selectionLimit={1}
                    />
                    {userAlreadySelected && (
                        <GuideMessageBar intent="warning">
                            <MessageBarBody>
                                <MessageBarTitle>Atención</MessageBarTitle>
                                El usuario seleccionado ya tiene accesos al contrato.
                            </MessageBarBody>
                        </GuideMessageBar>
                    )}
                </>
            )}

            {isEditMode && (
                <GuideField label="Usuario">
                    <Text>{props.CurrentAccess?.userName || props.CurrentAccess?.userID}</Text>
                </GuideField>
            )}

            <GuideDrawerSection title="Permisos">
                <Switch
                    label="Puede ver el contrato (lectura)"
                    checked={ReadAccess}
                    disabled={WriteAccess}
                    onChange={(_, data) => handleReadAccessChange(!!data.checked)}
                />
                <Switch
                    label="Puede editar el contrato (escritura)"
                    checked={WriteAccess}
                    onChange={(_, data) => handleWriteAccessChange(!!data.checked)}
                />
                {WriteAccess && (
                    <GuideMessageBar intent="info">
                        <MessageBarBody>
                            El permiso de escritura incluye automáticamente el permiso de lectura.
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
            </GuideDrawerSection>
        </GuideDrawer>
    );
}

export { ContractAccessFormComponent };

export default connect(
    OwnFunctions.mapStateToProps,
    OwnFunctions.mapDispatchToProps
)(ContractAccessFormComponent as any)
